import { useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react'

import { SOURCE_TYPES, type SourceKind } from '@/components/sources/AddSourceModal'

/**
 * The "Add a source" popover — ONE implementation shared by the app-generation
 * page, the builder hub and the Ask-AI dock (they used to hand-roll it three
 * times and drift apart).
 *
 * The caller owns the trigger button and the `open` state (each surface styles
 * its trigger differently); this owns the click-away backdrop, the popover
 * chrome, the row layout and the keyboard handling.
 *
 * Row layout notes: the icon sits in a fixed-width centred cell so labels line
 * up on a common left edge whatever the glyph's width, and hints clamp to two
 * lines (the Database hint is long enough to wrap five).
 */

export interface SourceMenuItem {
  /** Passed back to `onSelect` — a SourceKind, or a caller-defined action key. */
  key: string
  /** Classic Material Icons ligature (see the note on SOURCE_TYPES). */
  icon: string
  label: string
  hint: string
}

export const DEFAULT_SOURCE_ITEMS: SourceMenuItem[] = SOURCE_TYPES.map((t) => ({
  key: t.kind,
  icon: t.icon,
  label: t.label,
  hint: t.hint,
}))

// The composer's "+" menu mixes a plain file-picker action with the source
// kinds; this key distinguishes it from a SourceKind in `onSelect`. Shared so
// every "+" trigger (dock, builder hub, describe page) offers file attach
// identically instead of hand-rolling a separate paperclip button.
export const ATTACH_FILE_KEY = 'attach-file'
export const ATTACH_FILE_ITEM: SourceMenuItem = {
  key: ATTACH_FILE_KEY,
  icon: 'attach_file',
  label: 'Attach file',
  hint: 'Images, PDF / Word specs, text and data files',
}

/** Attach an existing IOFlow flow / example / application as a reference, so
 *  the agent can be told "build one like this". It becomes a plain text
 *  attachment, so it needs no separate plumbing beyond this menu row. */
export const ATTACH_IOFLOW_KEY = 'attach-ioflow'
export const ATTACH_IOFLOW_ITEM: SourceMenuItem = {
  key: ATTACH_IOFLOW_KEY,
  icon: 'account_tree',
  label: 'Attach IOFlow reference',
  hint: 'An existing flow, a prebuilt example, or a connector',
}

/** Connect (subscribe) an IOFlow application and test it with IOFlow's own
 *  connection test — opened by the user from the console, the same form an
 *  agent question opens. Opt-in per caller (needs a project). */
export const CONNECT_IOFLOW_APP_KEY = 'connect-ioflow-app'
export const CONNECT_IOFLOW_APP_ITEM: SourceMenuItem = {
  key: CONNECT_IOFLOW_APP_KEY,
  icon: 'power',
  label: 'Connect an application',
  hint: 'Connect a system your flows call (IOFlow) and test the connection',
}

interface Props {
  open: boolean
  onClose: () => void
  onSelect: (key: string) => void
  /** Defaults to every SOURCE_TYPES entry. */
  items?: SourceMenuItem[]
  /** Rows above the divider + section caption (e.g. the dock's "Attach file"). */
  leadingItems?: SourceMenuItem[]
  sectionLabel?: string
  /** 'top' opens upward (docked composers), 'bottom' downward (pages). */
  placement?: 'top' | 'bottom'
  align?: 'left' | 'right'
  minWidth?: number
  /** 'md' is the slightly larger type scale the dock uses. */
  size?: 'sm' | 'md'
}

export default function SourceMenu({
  open,
  onClose,
  onSelect,
  items = DEFAULT_SOURCE_ITEMS,
  leadingItems,
  sectionLabel = 'Add a source',
  placement = 'bottom',
  align = 'left',
  minWidth = 320,
  size = 'sm',
}: Props) {
  const menuRef = useRef<HTMLDivElement | null>(null)

  // Escape closes from anywhere while the popover is open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  // Arrow keys walk the rendered rows without needing a roving-tabindex model.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const buttons = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]') || [],
    )
    if (buttons.length === 0) return
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement)
    const step = e.key === 'ArrowDown' ? 1 : -1
    const next = current < 0
      ? (e.key === 'ArrowDown' ? 0 : buttons.length - 1)
      : (current + step + buttons.length) % buttons.length
    buttons[next].focus()
  }

  const labelSize = size === 'md' ? '0.9375rem' : '0.875rem'
  const hintSize = size === 'md' ? '0.8125rem' : '0.75rem'

  const renderRow = (item: SourceMenuItem) => (
    <button
      key={item.key}
      type="button"
      role="menuitem"
      onClick={() => onSelect(item.key)}
      style={rowStyle}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
      onFocus={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
      onBlur={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      <span style={iconCellStyle}>
        <span className="material-icons" style={{ fontSize: 19, lineHeight: 1, color: 'var(--color-text-secondary)' }}>
          {item.icon}
        </span>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 }}>
        <span style={{ fontSize: labelSize, fontWeight: 500, lineHeight: 1.3 }}>{item.label}</span>
        <span style={{ ...hintStyle, fontSize: hintSize }} title={item.hint}>
          {item.hint}
        </span>
      </span>
    </button>
  )

  return (
    <>
      {/* Click-away backdrop — keeps the menu a pure popover. */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
      <div
        ref={menuRef}
        role="menu"
        onKeyDown={onKeyDown}
        style={{
          position: 'absolute',
          ...(placement === 'top'
            ? { bottom: 'calc(100% + 0.375rem)' }
            : { top: 'calc(100% + 0.375rem)' }),
          ...(align === 'right' ? { right: 0 } : { left: 0 }),
          zIndex: 41,
          minWidth,
          maxWidth: 'min(400px, calc(100vw - 2rem))',
          padding: '0.25rem',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        }}
      >
        {leadingItems?.map(renderRow)}
        {leadingItems && leadingItems.length > 0 && (
          <div style={sectionCaptionStyle}>{sectionLabel}</div>
        )}
        {items.map(renderRow)}
      </div>
    </>
  )
}

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
  width: '100%',
  padding: '0.4375rem 0.625rem',
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'var(--color-text)',
}

// Fixed-width cell: labels align regardless of how wide the glyph renders.
const iconCellStyle: CSSProperties = {
  width: 20,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 2,
}

const hintStyle: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.35,
}

const sectionCaptionStyle: CSSProperties = {
  padding: '0.375rem 0.625rem 0.25rem',
  fontSize: '0.8125rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  borderTop: '1px solid var(--color-border)',
  marginTop: '0.25rem',
}

export type { SourceKind }
